import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SymbolKeepoIcon from "../../../../icons/SymbolKeepo";
import { BusinessCardPreview } from "../../../../services/api";
import { Card } from "./styles";

/** The Keepo the QR opens, marked as coming from the card (the same the wallets get, see BusinessCardWallet). */
export const qrUrlOf = (url: string) => {
  const address = new URL(url);
  address.searchParams.append("utm_source", "cartao_keepo");
  address.searchParams.append("utm_medium", "qr");
  return address.toString();
};

/** The host of the address of the Keepo ("anasouza.keepo.bio"); empty when there's no address to read. */
const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

type Props = {
  preview: BusinessCardPreview;
};

/** The business card as people see it: in the colors of the Keepo, the person on top, the QR of the Keepo below. */
export const CardPreview = ({ preview }: Props) => {
  const { t } = useTranslation();
  const [qr, setQr] = useState("");
  // without an address (it shouldn't happen: only published Keepos have a card) the card goes without a QR
  const host = hostOf(preview.url);

  useEffect(() => {
    if (!host) return;
    let current = true;
    QRCode.toString(qrUrlOf(preview.url), { type: "svg", margin: 0, errorCorrectionLevel: "M" })
      .then(svg => current && setQr(svg))
      .catch(() => current && setQr(""));
    return () => {
      current = false;
    };
  }, [preview.url, host]);

  return (
    <Card
      role="figure"
      aria-label={t("pages.businessCard.preview", { name: preview.name })}
      $background={preview.background}
      $foreground={preview.foreground}>
      <span className="logo">
        <SymbolKeepoIcon width={90} />
      </span>
      {preview.photo ? (
        <img className="photo" src={preview.photo} alt={preview.name} />
      ) : (
        <span className="photo" aria-hidden="true">
          {preview.name.trim().charAt(0)}
        </span>
      )}
      <p className="name">{preview.name}</p>
      {preview.role && <p className="role">{preview.role}</p>}
      {host && (
        <>
          {/* the code is drawn by the qrcode library (an svg of rectangles) */}
          <div
            className="qr"
            role="img"
            aria-label={t("pages.businessCard.qr", { url: host })}
            dangerouslySetInnerHTML={{ __html: qr }}
          />
          <span className="url">{host}</span>
        </>
      )}
    </Card>
  );
};
